import {useEffect, useState} from 'react';
import {IconCopy} from '@tabler/icons-react';
import {ActionIcon} from 'src/design/atoms/ActionIcon';
import type {ActionIconProps} from 'src/design/atoms/ActionIcon';

/**
 * "Copy to clipboard" action icon — copies `value` to the clipboard on
 * click, flipping its tooltip/accessible name to a "Copied"/"Failed to
 * copy" confirmation for 2 seconds before resetting.
 * @registry Icon-only "copy value to clipboard" action with inline
 * confirmation feedback.
 *
 * Ported from `library-m/components/action-icons/CopyActionIcon`. Two
 * deviations: (1) the original used `usehooks-ts`' `useCopyToClipboard`,
 * which isn't on the `design/` import whitelist — rewritten against
 * `navigator.clipboard.writeText` directly, preserving the same
 * success/error branching and 2s open-then-reset tooltip timing; (2) the
 * default `size` changed from the original's invalid `'small'` token to
 * `'sm'`, matching the underlying atom's own default.
 */
export interface CopyActionIconProps
  extends Omit<ActionIconProps, 'onClick' | 'tooltipText' | 'tooltipProps'> {
  value: string;
  onCopySuccess?: (value: string) => void;
  onCopyError?: (value: string) => void;
  iconSize?: number;
}

export const CopyActionIcon = ({
  value,
  onCopySuccess,
  onCopyError,
  iconSize,
  color = 'black',
  variant = 'subtle',
  size = 'sm',
  ...props
}: CopyActionIconProps) => {
  const [tooltipText, setTooltipText] = useState('Copy to clipboard');
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    if (!tooltipOpen) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setTooltipOpen(false);
      setTooltipText('Copy to clipboard');
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [tooltipOpen]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setTooltipText(`Copied ${value}`);
      setTooltipOpen(true);
      onCopySuccess?.(value);
    } catch {
      setTooltipText(`Failed to copy ${value}`);
      setTooltipOpen(true);
      onCopyError?.(value);
    }
  };

  return (
    <ActionIcon
      color={color}
      variant={variant}
      size={size}
      {...props}
      onClick={handleCopy}
      tooltipText={tooltipText}
      tooltipProps={{
        opened: tooltipOpen,
        openDelay: 0,
        closeDelay: 500,
      }}>
      <IconCopy size={iconSize} />
    </ActionIcon>
  );
};
